import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Lookup from '../../entities/lookup';
import Blackout from '../../entities/blackout';
import Discount from '../../entities/discount';
import Document from '../../entities/document';
import Expense from '../../entities/expense';
import Event from '../../entities/event';
import Identification from '../../entities/identification';
import Milestone from '../../entities/milestone';
import Participant from '../../entities/participant';
import TimeOff from '../../entities/timeOff';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
export function canDeleteLookup(): boolean {
return true;
}
export function collectLookupDeleteImpact(lookup: Lookup, context: DeleteImpactContext): void {
const key = 'lookup:' + lookup.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const blackoutReasonImpact = workingRows<Blackout>('blackout', findAll<Blackout>('blackouts')).filter(blackoutRef => blackoutRef.reason === lookup.id);
blackoutReasonImpact.forEach(blackoutRef => addDeleteImpact(context, 'blackout:' + blackoutRef.id, 'blackouts*'));
const discountReasonImpact = workingRows<Discount>('discount', findAll<Discount>('discounts')).filter(discountRef => discountRef.reason === lookup.id);
discountReasonImpact.forEach(discountRef => addDeleteImpact(context, 'discount:' + discountRef.id, 'discounts*'));
const documentTagsImpact = workingRows<Document>('document', findAll<Document>('documents')).filter(documentRef => documentRef.tags?.includes(lookup.id));
documentTagsImpact.forEach(documentRef => addDeleteImpact(context, 'document:' + documentRef.id, 'documents'));
const expenseExpenseTypeImpact = workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expenseRef => expenseRef.expenseType === lookup.id);
expenseExpenseTypeImpact.forEach(expenseRef => addDeleteImpact(context, 'expense:' + expenseRef.id, 'expenses*'));
const eventCategoryImpact = workingRows<Event>('event', findAll<Event>('events')).filter(eventRef => eventRef.category === lookup.id);
eventCategoryImpact.forEach(eventRef => addDeleteImpact(context, 'event:' + eventRef.id, 'events*'));
const identificationMethodTypeImpact = workingRows<Identification>('identification', findAll<Identification>('identifications')).filter(identificationRef => identificationRef.methodType === lookup.id);
identificationMethodTypeImpact.forEach(identificationRef => addDeleteImpact(context, 'identification:' + identificationRef.id, 'identifications*'));
const milestoneReasonsImpact = workingRows<Milestone>('milestone', findAll<Milestone>('milestones')).filter(milestoneRef => milestoneRef.reasons?.includes(lookup.id));
milestoneReasonsImpact.forEach(milestoneRef => addDeleteImpact(context, 'milestone:' + milestoneRef.id, 'milestones'));
const participantRelationshipsImpact = workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participantRef => participantRef.relationships?.includes(lookup.id));
participantRelationshipsImpact.forEach(participantRef => addDeleteImpact(context, 'participant:' + participantRef.id, 'participants*'));
const timeOffReasonImpact = workingRows<TimeOff>('timeOff', findAll<TimeOff>('timeOffs')).filter(timeOffRef => timeOffRef.reason === lookup.id);
timeOffReasonImpact.forEach(timeOffRef => addDeleteImpact(context, 'timeOff:' + timeOffRef.id, 'timeOffs*'));}
export function deleteImpactLookup(lookup: Lookup): DeleteImpact {
const context = deleteImpactContext();
collectLookupDeleteImpact(lookup, context);
return deleteImpactCounts(context);
}
export function deleteLookup(lookup: Lookup, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteLookup()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectLookupDeleteImpact(lookup, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || lookup.id};
}
const key = 'lookup:' + lookup.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Document>('document', [...store.getState().documents.getMappingFor('tags', lookup.id)]).filter(documentTemp => documentTemp.tags?.includes(lookup.id)).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_document.tags = _document.tags?.filter(id => id !== lookup.id) || [];

_work.set('document:' + _document.id, _document);
dispatch(Actions.DocumentActions.saveDocumentQueue(_document), 'document:' + _document.id);
});
workingRows<Milestone>('milestone', [...store.getState().milestones.getMappingFor('reasons', lookup.id)]).filter(milestoneTemp => milestoneTemp.reasons?.includes(lookup.id)).forEach(milestoneTemp => {
const _milestone = ((_work.get('milestone:' + milestoneTemp.id) ?? milestoneTemp) as Milestone).clone();
if (_milestone.status === 'D') return;
_milestone.status = 'U';
_milestone.addedOn = newHRTimestamp();
_milestone.addedBy = getCurrentUserId();
_milestone.importSource = targetImportSource;
_milestone.importId = mergeId;
_milestone.reasons = _milestone.reasons?.filter(id => id !== lookup.id) || [];

_work.set('milestone:' + _milestone.id, _milestone);
dispatch(Actions.MilestoneActions.saveMilestoneQueue(_milestone), 'milestone:' + _milestone.id);
});
workingRows<Participant>('participant', [...store.getState().participants.getMappingFor('relationships', lookup.id)]).filter(participantTemp => (participantTemp.relationships?.includes(lookup.id) && participantTemp.relationships.some(other => other !== lookup.id))).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
if (_participant.relationships?.some(other => other !== lookup.id)) _participant.relationships = _participant.relationships.filter(id => id !== lookup.id);

_work.set('participant:' + _participant.id, _participant);
dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant), 'participant:' + _participant.id);
});
const deleted = lookup.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.LookupActions.removeLookupQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceLookup(lookup: Lookup, replaceWith: Lookup, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = lookup.id;}
const _work = workingSet();
workingRows<Blackout>('blackout', [...store.getState().blackouts.getMappingFor('reason', lookup.id)]).filter(blackoutTemp => blackoutTemp.reason === lookup.id).forEach(blackoutTemp => {
const _blackout = ((_work.get('blackout:' + blackoutTemp.id) ?? blackoutTemp) as Blackout).clone();
if (_blackout.status === 'D') return;
_blackout.status = 'U';
_blackout.addedOn = newHRTimestamp();
_blackout.addedBy = getCurrentUserId();
_blackout.importSource = targetImportSource;
_blackout.importId = mergeId;
if (_blackout.reason === lookup.id) {
_blackout.reason = replaceWith.id;

}
_work.set('blackout:' + _blackout.id, _blackout);
store.dispatch(Actions.BlackoutActions.saveBlackoutQueue(_blackout));
});
workingRows<Discount>('discount', [...store.getState().discounts.getMappingFor('reason', lookup.id)]).filter(discountTemp => discountTemp.reason === lookup.id).forEach(discountTemp => {
const _discount = ((_work.get('discount:' + discountTemp.id) ?? discountTemp) as Discount).clone();
if (_discount.status === 'D') return;
_discount.status = 'U';
_discount.addedOn = newHRTimestamp();
_discount.addedBy = getCurrentUserId();
_discount.importSource = targetImportSource;
_discount.importId = mergeId;
if (_discount.reason === lookup.id) {
_discount.reason = replaceWith.id;

}
_work.set('discount:' + _discount.id, _discount);
store.dispatch(Actions.DiscountActions.saveDiscountQueue(_discount));
});
workingRows<Document>('document', [...store.getState().documents.getMappingFor('tags', lookup.id)]).filter(documentTemp => documentTemp.tags?.includes(lookup.id)).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_document.tags = _document.tags?.map(id => (id === lookup.id) ? replaceWith.id : id) || [];

_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('expenseType', lookup.id)]).filter(expenseTemp => expenseTemp.expenseType === lookup.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.expenseType === lookup.id) {
_expense.expenseType = replaceWith.id;

}
_work.set('expense:' + _expense.id, _expense);
store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
});
workingRows<Event>('event', [...store.getState().events.getMappingFor('category', lookup.id)]).filter(eventTemp => eventTemp.category === lookup.id).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
if (_event.category === lookup.id) {
_event.category = replaceWith.id;

}
_work.set('event:' + _event.id, _event);
store.dispatch(Actions.EventActions.saveEventQueue(_event));
});
workingRows<Identification>('identification', [...store.getState().identifications.getMappingFor('methodType', lookup.id)]).filter(identificationTemp => identificationTemp.methodType === lookup.id).forEach(identificationTemp => {
const _identification = ((_work.get('identification:' + identificationTemp.id) ?? identificationTemp) as Identification).clone();
if (_identification.status === 'D') return;
_identification.status = 'U';
_identification.addedOn = newHRTimestamp();
_identification.addedBy = getCurrentUserId();
_identification.importSource = targetImportSource;
_identification.importId = mergeId;
if (_identification.methodType === lookup.id) {
_identification.methodType = replaceWith.id;

}
_work.set('identification:' + _identification.id, _identification);
store.dispatch(Actions.IdentificationActions.saveIdentificationQueue(_identification));
});
workingRows<Milestone>('milestone', [...store.getState().milestones.getMappingFor('reasons', lookup.id)]).filter(milestoneTemp => milestoneTemp.reasons?.includes(lookup.id)).forEach(milestoneTemp => {
const _milestone = ((_work.get('milestone:' + milestoneTemp.id) ?? milestoneTemp) as Milestone).clone();
if (_milestone.status === 'D') return;
_milestone.status = 'U';
_milestone.addedOn = newHRTimestamp();
_milestone.addedBy = getCurrentUserId();
_milestone.importSource = targetImportSource;
_milestone.importId = mergeId;
_milestone.reasons = _milestone.reasons?.map(id => (id === lookup.id) ? replaceWith.id : id) || [];

_work.set('milestone:' + _milestone.id, _milestone);
store.dispatch(Actions.MilestoneActions.saveMilestoneQueue(_milestone));
});
workingRows<Participant>('participant', [...store.getState().participants.getMappingFor('relationships', lookup.id)]).filter(participantTemp => participantTemp.relationships?.includes(lookup.id)).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
_participant.relationships = _participant.relationships?.map(id => (id === lookup.id) ? replaceWith.id : id) || [];

_work.set('participant:' + _participant.id, _participant);
store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
});
workingRows<TimeOff>('timeOff', [...store.getState().timeOffs.getMappingFor('reason', lookup.id)]).filter(timeOffTemp => timeOffTemp.reason === lookup.id).forEach(timeOffTemp => {
const _timeOff = ((_work.get('timeOff:' + timeOffTemp.id) ?? timeOffTemp) as TimeOff).clone();
if (_timeOff.status === 'D') return;
_timeOff.status = 'U';
_timeOff.addedOn = newHRTimestamp();
_timeOff.addedBy = getCurrentUserId();
_timeOff.importSource = targetImportSource;
_timeOff.importId = mergeId;
if (_timeOff.reason === lookup.id) {
_timeOff.reason = replaceWith.id;

}
_work.set('timeOff:' + _timeOff.id, _timeOff);
store.dispatch(Actions.TimeOffActions.saveTimeOffQueue(_timeOff));
});



return lookup;
}
